import axios from 'axios';
import type { Bond, UploadBondsResponse, GetBondsResponse } from '../types/bonds';
import { generateTempId } from '../utils/idUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get auth credentials from localStorage
const getAuthCredentials = () => {
  try {
    const savedCredentials = localStorage.getItem('itp_auth_credentials');
    return savedCredentials ? JSON.parse(savedCredentials) : null;
  } catch (error) {
    console.error('Failed to parse auth credentials:', error);
    return null;
  }
};

// Request interceptor for authentication and logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);

    // Add authentication headers if credentials are available
    const credentials = getAuthCredentials();
    if (credentials) {
      config.headers['X-Username'] = credentials.username;
      config.headers['X-Password'] = credentials.password;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const bondsApi = {
  getBonds: async (): Promise<GetBondsResponse> => {
    const response = await apiClient.get<GetBondsResponse>('/bonds');
    return response.data;
  },

  updateBonds: async (bonds: Bond[], fileName?: string): Promise<Bond[]> => {
    const payload: { bonds: Bond[]; fileName?: string } = { bonds };
    if (fileName) {
      payload.fileName = fileName;
    }
    const response = await apiClient.put<Bond[]>('/bonds', payload);
    return response.data;
  },

  uploadBondsFromExcel: async (file: File): Promise<{ bonds: Bond[]; fileName: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('importAction', 'return');

    const response = await apiClient.post<UploadBondsResponse>('/bonds/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Generate temporary IDs for imported bonds that don't have IDs
    const bondsWithIds = response.data.bonds.map((bond) => ({
      ...bond,
      id: generateTempId()
    }));

    return {
      bonds: bondsWithIds,
      fileName: response.data.fileName
    };
  },
};

export default apiClient; 